import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdTextarea,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import ApiService from '../../Services/ApiService';
import PermissionsService, { PERMISSION_REFUELING_CARDS } from '../../Services/PermissionsService';
import { IImportsInput } from './ImportRecord';
import usePageWithForm from '../../hooks/usePageWithForm';
import { transformErrors, validateFields } from '../../Services/HookFormService';

const RESPONSE_OK = 'ok';

interface ModalFormProps {
  handleClose: () => void;
  data: IImportsInput[];
  id: number | null;
  customerId: number | null;
  updateTable: () => void;
}

type FormValues = {
  note: string;
};

type FormValidation = {
  note: IValidationRule[];
};

type IValidationRule = {
  param: string;
  value: boolean | number[] | string | null;
  message: string | null;
};

export default function Form(props: ModalFormProps): JSX.Element {
  usePageWithForm();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const { formatMessage: f } = useIntl();
  const { id } = props;
  const [validation, setValidation] = React.useState<FormValidation | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      note: '',
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, validation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
    context: { validation },
  });

  // update
  const update = async (updateValues) => {
    if (id) {
      setIsLoaderActive(true);

      const response = await ApiService.put(
        `/v1/expense/import/${id}/note/edit?customerId=${props.customerId}`,
        updateValues
      );
      setIsLoaderActive(false);
      if (response.data && response.data.status === RESPONSE_OK) {
        props.handleClose();
        props.updateTable();
      }
    }
  };

  const handleFormData = (data: IImportsInput[], type: string) => {
    return {
      note: data.find((i) => i.indexId === 'note')?.[type],
    };
  };

  React.useEffect(() => {
    if (props.data) {
      setValue('note', (handleFormData(props.data, 'value').note || '') as string);
      setValidation(handleFormData(props.data, 'rules'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // modal adding title and buttons
  let buttons: CmdModalFooterButton[] = [
    {
      id: 'modalClose',
      type: 'button',
      title: f({ id: 'form.close' }),
      className: 'e-button',
      closeCallback: props.handleClose,
      tooltip: f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
    },
    {
      id: 'submitAddForm',
      type: 'submit',
      title: f({ id: 'form.save' }),
      className: 'e-button e-button--gray',
      submitCallback: handleSubmit(update),
      tooltip: f({ id: 'alarms.modal.recipient.save' }),
    },
  ];
  let isAllowed = true;

  // modal editing title and buttons
  if (id) {
    buttons = [
      {
        id: 'modalClose',
        type: 'button',
        title: f({ id: 'form.close' }),
        className: 'e-button',
        closeCallback: props.handleClose,
        tooltip: f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
      },
    ];
    isAllowed = PermissionsService.hasAccess(
      PERMISSION_REFUELING_CARDS,
      ['update'],
      props.customerId
    );
    if (isAllowed) {
      buttons.push({
        id: 'submitEditForm',
        type: 'submit',
        title: f({ id: 'form.save' }),
        className: 'e-button e-button--gray',
        submitCallback: handleSubmit(update),
      });
    }
  }

  return (
    <CmdModal
      id="import-note-form"
      title={f({ id: 'table.detail' })}
      footerButtons={buttons}
      loader={isLoaderActive}
    >
      {isAllowed ? (
        <div>
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <CmdTextarea
                {...field}
                id="note"
                label={f({ id: 'form.inputs.note' })}
                rows={5}
                required={true}
                setValue={(value) => {
                  setValue('note', value);
                  trigger('note');
                }}
                error={errors.note?.message}
                touched={!!errors.note}
              />
            )}
          />
        </div>
      ) : (
        <div className="form-horizontal">
          <CmdLabel title={f({ id: 'form.inputs.note' })} value={watch('note')} areLabels={true} />
        </div>
      )}
    </CmdModal>
  );
}
